/**
 * Strip internal AI prompt artifacts from display — ported verbatim from
 * Outbound's `client/src/utils/aiText.ts` so the CRM's Outbound-sourced
 * research surfaces render exactly like Outbound (no `[role-based]`, no
 * Perplexity-style `[2]` citation markers).
 *
 * The AI research pipeline leaves prompt-engineering markers in some
 * generated fields:
 *
 *   [SOURCE FACTS]   [FACTS]   [END FACTS]   [role-based]
 *
 * Those are useful internally (the LLM was instructed to wrap evidence
 * blocks in them) but must NEVER reach end users. This util is the single
 * point of presentation-layer cleanup.
 *
 * Important: this does NOT mutate any stored record. The Outbound backend
 * keeps the raw text; only the rendered string is cleaned here.
 */

// Tag set is intentionally narrow + literal — we only remove tags the
// Outbound pipeline is known to generate.
const INTERNAL_TAGS: ReadonlyArray<RegExp> = [
  /\[SOURCE FACTS\]/gi,
  /\[FACTS\]/gi,
  /\[END FACTS\]/gi,
  /\[role-based\]/gi,
  // Perplexity-style citation markers like `[1]`, `[9]`, `[10]` are
  // useful to the AI pipeline but confusing for non-technical users
  // reading the rendered Summary / Pain Points / Social Signals. Strip
  // both single (`[9]`) and consecutive (`[1][5]`) forms. Numbers-only
  // inside the brackets prevents collateral on legitimate text like
  // `[a real footnote]` or `[year]`.
  /\[\d+\]/g,
];

/**
 * Strip the known internal tags + tidy resulting whitespace so the
 * rendered surface doesn't show double blank lines or leading
 * indentation left behind by the removed token.
 */
export function stripInternalAiTags(input: string | null | undefined): string {
  if (!input) return '';
  let out = input;
  for (const re of INTERNAL_TAGS) {
    out = out.replace(re, '');
  }
  // Citation strip leaves orphan whitespace patterns like `services . `
  // (space before period) or `word  word` (double space). Tidy them so
  // the rendered prose reads naturally.
  out = out.replace(/[ \t]+([.,;:!?])/g, '$1'); // " ." → "."
  out = out.replace(/[ \t]{2,}/g, ' '); // "  " → " "
  out = out.replace(/\n{3,}/g, '\n\n');
  out = out.replace(/^[ \t]+/gm, '');
  return out.trim();
}

/**
 * Convenience for list-shaped fields like `painPoints[]` so callers don't
 * need to map+filter inline. Drops entries that become empty after the
 * strip.
 */
export function stripInternalAiTagsFromList(
  items: ReadonlyArray<string | null | undefined> | null | undefined,
): string[] {
  if (!items) return [];
  const out: string[] = [];
  for (const v of items) {
    const cleaned = stripInternalAiTags(v);
    if (cleaned) out.push(cleaned);
  }
  return out;
}
